import {Component, OnInit, Optional} from "@angular/core";
import {Router} from "@angular/router";
import {
    FUIAlertAction,
    FUIAlertActions,
    FUIAlertService,
    FUISpinnersService,
    FUIStackedPageContextService
} from "@fedelta-pos/ui";
import {ErrorHandlerService} from "../../../error-handler.service";
import {CustomerInstanceVenueService} from "../customer-instance-venue.service";
import {CustomerInstanceVenueSearchData} from "../customer-instance-venue-search.data";
import {CustomerInstanceVenueData} from "../customer-instance-venue.data";

@Component({
    selector: "fhm-customer-instance-venue-search-page",
    styleUrls: ["customer-instance-venue-search-page.component.scss"],
    templateUrl: "./customer-instance-venue-search-page.component.html",
    providers: [CustomerInstanceVenueService],
    standalone: false
})
export class CustomerInstanceVenueSearchPageComponent implements OnInit {

    gridData: CustomerInstanceVenueSearchData[] = [];
    data: CustomerInstanceVenueSearchData[] = [];

    filterName: string;
    filterClientId: string;
    filterDeploymentStatus: string;
    filterRequiresAction: boolean;

    searchMode: boolean;
    noInstancesOnly: boolean;
    showDiscontinued: boolean;

    constructor(
                private router: Router,
                private errorHandler: ErrorHandlerService,
                private spinnersService: FUISpinnersService,
                private customerInstanceVenueService: CustomerInstanceVenueService,
                private alertService: FUIAlertService,
                @Optional() private stackedPageContextService: FUIStackedPageContextService) {
    }

    ngOnInit(): void {
        if (this.stackedPageContextService) {
            if (this.stackedPageContextService.params.searchMode) {
                this.stackedPageContextService.title = "Customer Instances";
                this.searchMode = true;
            }
            this.noInstancesOnly = this.stackedPageContextService.params.noInstancesOnly;
        }

        this.load();
    }

    async load(): Promise<void> {
        this.spinnersService.open();
        try {
            this.data = await this.customerInstanceVenueService.getCustomerInstanceVenues();
            this.updateFilter();
        } catch (error) {
            this.errorHandler.handleErrorResponse(error);
        } finally {
            this.spinnersService.close();
        }
    }

    recordSelected(record: any): void {
        if (this.searchMode) {
            this.stackedPageContextService.confirm(record.id);
        } else {
            this.router.navigate(["/main/customer-instance-venue-edit/" + record["id"]]);
        }
    }

    createNew(): void {
        this.router.navigate(["/main/customer-instance-venue-edit/new"]);
    }

    changeDeploymentStatus(deploymentStatus: string): void {
        this.filterDeploymentStatus = deploymentStatus;
        this.updateFilter();
    }

    updateFilter(): void {
        this.gridData = this.data.filter((record: CustomerInstanceVenueSearchData) => {
            if (this.filterName && record.name.toLowerCase().indexOf(this.filterName.toLowerCase()) === -1) {
                return false;
            } else if (this.filterClientId && record.clientId.toLowerCase().indexOf(this.filterClientId.toLowerCase()) === -1) {
                return false;
            } else if (this.filterDeploymentStatus && record.deploymentStatus !== this.filterDeploymentStatus) {
                return false;
            } else if (this.filterRequiresAction && !record.requiresBilling && !record.requiresContractDirectorSign
                && !record.requiresContractSign && !record.requiresAppPaymentAccount && !record.requiresSalesHandover) {
                return false;
            } else if (this.noInstancesOnly && !!record.clientId) {
                return false;
            } else if (!this.showDiscontinued && record.discontinued) {
                return false;
            }
            return true;
        });
    }

    async refresh(): Promise<void> {
        await this.load();
    }

    styleColumnFn(columnName: string, record: CustomerInstanceVenueSearchData): any {
        if (columnName === "requiresAction" && (record.requiresBilling || record.requiresContractSign || record.requiresContractDirectorSign
            || record.requiresAppPaymentAccount || record.requiresSalesHandover)) {
            return {"color": "red", "font-weight": "bold"};
        }

        return undefined;
    }

    valueColumnFn(columnName: string, record: CustomerInstanceVenueSearchData): any {
        if (columnName === "requiresAction") {
            const actions: string[] = [];
            if (record.requiresContractSign) {
                actions.push("Contract Sign");
            }
            if (record.requiresContractDirectorSign) {
                actions.push("Contract Director Sign");
            }
            if (record.requiresBilling) {
                actions.push("Direct Debit");
            }
            if (record.requiresAppPaymentAccount) {
                actions.push("Payment Account (Stripe)");
            }
            if (record.requiresSalesHandover) {
                actions.push("Sales Handover");
            }
            return actions.length === 0 ? "" : ("Requires " + actions.join(", "));
        }

        return record[columnName];
    }

    showDelete(record: any): boolean {
        return !record.discontinued;
    }

    showRecontinue(record: any): boolean {
        return record.discontinued;
    }

    async deleteVenue(record: any): Promise<void> {
        if (record.deploymentStatus === CustomerInstanceVenueData.DEPLOYMENT_STATUS_PENDING) {
            const action: FUIAlertAction = await this.alertService.showWarningOkCancel("fhm.customer-instance-venue.alert.delete.title",
                "fhm.customer-instance-venue.alert.delete.message").toPromise();

            if (action === FUIAlertActions.OK_ACTION) {
                this.spinnersService.open();
                try {
                    await this.customerInstanceVenueService.deleteVenue(record.id);
                    this.spinnersService.close();
                } catch (e) {
                    this.spinnersService.close();
                    this.errorHandler.handleErrorResponse(e);
                }
                await this.refresh();
            }
        } else {
            this.discontinueVenue(record);
        }
    }

    async discontinueVenue(record: any): Promise<void> {
        const action: FUIAlertAction = await this.alertService.showWarningOkCancel("fhm.customer-instance-venue.alert.discontinue.title",
            "fhm.customer-instance-venue.alert.discontinue.message").toPromise();
        if (action === FUIAlertActions.OK_ACTION) {
            this.spinnersService.open();
            try {
                await this.customerInstanceVenueService.discontinueVenue(record.id);
                this.spinnersService.close();
            } catch (e) {
                this.spinnersService.close();
                this.errorHandler.handleErrorResponse(e);
            }
            await this.refresh();
        }
    }

    async recontinueVenue(record: any): Promise<void> {
        const action: FUIAlertAction = await this.alertService.showWarningOkCancel("fhm.customer-instance-venue.alert.recontinue.title",
            "fhm.customer-instance-venue.alert.recontinue.message").toPromise();
        if (action === FUIAlertActions.OK_ACTION) {
            this.spinnersService.open();
            try {
                await this.customerInstanceVenueService.recontinueVenue(record.id);
                this.spinnersService.close();
            } catch (e) {
                this.spinnersService.close();
                this.errorHandler.handleErrorResponse(e);
            }
            await this.refresh();
        }
    }
}
